/**
 * Publishes the signed-in organization - and the signed-out state - where AI Studio can read it.
 *
 * - AI Studio holds COPIES of our tokens, on its own subdomain.
 * - Switching organization mints new tokens here and leaves those copies working, so it keeps
 *   showing the PREVIOUS organization. Logging out doesn't touch them either: they go on working
 *   until the access token expires.
 * - It can't detect either by itself: its token IS the organization, and it stays valid.
 * - So: we write what's true here to a cookie on the parent domain, it compares (`orgSession.ts`).
 *
 * Convenience, not security - the old token stays valid until the backend revokes it.
 */

/** The name AI Studio reads. Changing it means changing it in both apps. */
const ORG_COOKIE = 'imagine_current_org';

/**
 * What we publish when nobody is signed in.
 *
 * Deleting the cookie instead - which is what this used to do - is indistinguishable from never
 * having written one, and AI Studio has to read that silence as "no information": an older shell,
 * a domain that can't share the cookie, cookies switched off. None of those may throw anyone out,
 * so signing out has to SAY something or it says nothing at all.
 *
 * Never collides with an organization id, which is numeric.
 */
const SIGNED_OUT = 'none';

/**
 * Scopes the cookie so every `*.imagine.io` app gets it.
 *
 * - Host-only when there's no parent domain to share, which suits local dev: cookies ignore the
 *   port, so `localhost:3000` and `localhost:3001` already share them.
 * - IP hosts get host-only too. `127.0.0.1` would ask for `domain=.0.1`, which matches nothing,
 *   and the browser then drops the whole cookie silently.
 */
function cookieDomain() {
  const { hostname } = window.location;
  if (/^[\d.]+$/.test(hostname)) return '';
  const parts = hostname.split('.');
  if (parts.length < 2) return '';
  return `; domain=.${parts.slice(-2).join('.')}`;
}

const secure = () => (window.location.protocol === 'https:' ? '; secure' : '');

const write = (value) => {
  document.cookie = `${ORG_COOKIE}=${value}; path=/${cookieDomain()}; SameSite=Lax${secure()}`;
};

/** Tell the other apps which organization is signed in. */
export const publishCurrentOrg = (orgId) => {
  if (!orgId) return;
  write(encodeURIComponent(String(orgId)));
};

/** Tell them nobody is - the only thing that ends AI Studio's session, since its tokens don't. */
export const publishSignedOut = () => write(SIGNED_OUT);
